import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import { Server, LogOut, User, FileText, Shield } from 'lucide-react';
import Login from './components/Login';
import HomePage from './components/HomePage';
import Dashboard from './components/Dashboard';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import CodeRain from './components/CodeRain';
import './App.css';

// Pour le développement local: const socket = io('http://localhost:40000');
// Pour le déploiement VPS: const socket = io();
const socket = io();

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState({});
  const [guilds, setGuilds] = useState([]);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'dashboard', 'terms', 'privacy'
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    // Check authentication status
    fetchUser();

    // Socket.IO real-time updates
    socket.on('maintenance-update', (data) => {
      setMaintenanceData(data);
    });

    return () => {
      socket.off('maintenance-update');
    };
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch('/api/auth/user');
      const data = await response.json();
      if (data.authenticated) {
        setUser(data.user);
        setIsAuthenticated(true);
        setCurrentView('dashboard'); // Redirect to dashboard after login
        fetchGuilds();
        fetchMaintenanceStatus();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchGuilds = async () => {
    try {
      const response = await fetch('/api/guilds');
      const data = await response.json();
      setGuilds(data);
    } catch (error) {
      console.error('Error fetching guilds:', error);
    }
  };

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await fetch('/api/maintenance');
      const data = await response.json();
      setMaintenanceData(data);
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
      setIsAuthenticated(false);
      setUser(null);
      setCurrentView('home');
      setShowLogin(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleShowLogin = () => {
    setShowLogin(true);
  };

  const handleBackToHome = () => {
    setShowLogin(false);
  };

  const updateMaintenance = async (guildId, data) => {
    try {
      const response = await fetch(`/api/maintenance/${guildId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (response.ok) {
        setMaintenanceData(prev => ({
          ...prev,
          [guildId]: result
        }));
      } else {
        alert(result.error || 'Erreur lors de la mise à jour');
      }
      return result;
    } catch (error) {
      console.error('Error updating maintenance:', error);
      alert('Erreur lors de la mise à jour');
      return null;
    }
  };

  if (!isAuthenticated) {
    if (showLogin) {
      return <Login onBackToHome={handleBackToHome} />;
    }
    return <HomePage onShowLogin={handleShowLogin} isAuthenticated={false} />;
  }

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return <HomePage onShowLogin={handleShowLogin} isAuthenticated={true} onGoToDashboard={() => setCurrentView('dashboard')} />;
      case 'terms':
        return <TermsOfService />;
      case 'privacy':
        return <PrivacyPolicy />;
      default:
        return (
          <Dashboard
            guilds={guilds}
            maintenanceData={maintenanceData}
            onUpdateMaintenance={updateMaintenance}
            user={user}
          />
        );
    }
  };

  return (
    <div className="app">
      <CodeRain />
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <img src="/logo.png" alt="Logo" className="logo-image" />
            <h1>Discord Maintenance</h1>
          </div>
          <div className="header-actions">
            <button 
              className="home-btn" 
              onClick={() => setCurrentView('home')}
              title="Retour à l'accueil"
            >
              <Server size={20} />
              Accueil
            </button>
            <div className="user-info">
              {user?.avatar ? (
                <img 
                  src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`} 
                  alt="Avatar"
                  className="user-avatar"
                />
              ) : (
                <User className="user-icon" size={24} />
              )}
              <span className="username">{user?.username}</span>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              <LogOut size={20} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>
      <main className="app-main">
        {renderContent()}
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <p>© {new Date().getFullYear()} Discord Maintenance Panel</p>
          <div className="footer-links">
            <button onClick={() => setCurrentView('terms')} className={currentView === 'terms' ? 'active' : ''}>
              <FileText size={16} />
              CGU
            </button>
            <button onClick={() => setCurrentView('privacy')} className={currentView === 'privacy' ? 'active' : ''}>
              <Shield size={16} />
              Politique de confidentialité
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
